import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { CalendarOptions } from '@fullcalendar/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import { bmxurl, ConvertDateToDB, getAccess } from 'src/globals';
import { booking_returndata } from 'src/types/booking_returndata';
import { users_returndata } from 'src/types/user_returndata';
import { venue_returndata } from 'src/types/venue_returndata';
import { BusyService } from '../_services/busy.service';
import { GlobalService } from '../_services/global.service';
import { ItineraryService } from '../_services/itinerary.service';
import { DaysheetOrderService } from '../_services/daysheet-order.service';

@Component({
  selector: 'app-bookingcalendar',
  templateUrl: './bookingcalendar.component.html',
  styleUrls: ['./bookingcalendar.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BookingcalendarComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  httpResponse: any;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;

  chosenDate: Date = new Date();

  //The view switch lives in the page header with the date controls rather than in the calendar's
  //own toolbar, so it matches every other screen and can be made a sensible size.
  @ViewChild('bookingCalendar') calendarRef: FullCalendarComponent;
  calendarView: string = 'timeGridDay';
  viewOptions: any[] = [
    {label: 'Day', value: 'timeGridDay'},
    {label: 'List', value: 'listDay'}
  ];

  Venue: any;
  resources: any[] = [];
  //Which resources the calendar shows, from the Daysheet Resource Order page - null until it loads.
  private visible: Map<string, boolean> = null;
  //A booking is coloured by its package, the way the daysheet colours its blocks. BookingList
  //does not return the colour, so the packages are read and matched up here.
  private packageColours = new Map<string, string>();
  bookingCount: number = 0;

  calendarOptions: CalendarOptions = {
    initialView: 'timeGridDay',
    plugins: [timeGridPlugin, listPlugin],
    //Off - the switch is in the page header instead.
    headerToolbar: false,
    allDaySlot: false,
    height: 'auto',
    nowIndicator: true,
    slotDuration: '00:15:00',
    //Bookings at the same time sit side by side rather than on top of one another, and a short
    //one is never drawn smaller than its text needs.
    slotEventOverlap: false,
    eventMinHeight: 44,
    expandRows: true,
    events: []
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService,
    private itineraryService: ItineraryService,
    private daysheetOrder: DaysheetOrderService) { }

  ngOnInit(): void {

    this.globalService.setSmallScreenMenu(false);

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata);

      if (this.httpResponse.succeeded) {
        this.AccessAllowed = getAccess("MAKEABOOKING", this.httpResponse.data) == true;

        this.loadVenue();
        this.loadResources();
        this.loadPackages();
        this.loadgrid();
      }

      this.CompletedLoading = true;
    }, error => {
      this.CompletedLoading = true;
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User.'});
    });
  }

  //The venue's own opening hours decide how much of the day the grid shows, rather than all 24.
  private loadVenue() {
    this.http.get(bmxurl + "venue/" + this.VenueID).subscribe(response => {
      this.Venue = (response as venue_returndata);

      this.calendarOptions = {
        ...this.calendarOptions,
        slotMinTime: this.slotTime(this.Venue?.data?.startTime, '08:00:00'),
        slotMaxTime: this.slotTime(this.Venue?.data?.endTime, '23:00:00')
      };
    }, error => {
      //Not fatal - the grid just shows the default hours.
    });
  }

  //The venue holds its hours as HH:mm; the grid wants HH:mm:ss.
  private slotTime(value: string, fallback: string): string {
    if (value == null || value.length < 5) {
      return fallback;
    }

    return value.length == 5 ? value + ":00" : value;
  }

  /*
    Which resources the calendar shows come from the Daysheet Resource Order page, the same as the
    Daysheet and the itineraries - see ItineraryService.fromDaysheetOrder. The order comes with the
    resource list itself, which is where the colours come from.
  */
  private loadResources() {
    this.daysheetOrder.load(this.VenueID).subscribe(result => {
      this.resources = result.items.filter(item => item.resource != null).map(item => item.resource);

      if (result.failed) {
        //Nothing to go by, so everything is shown rather than nothing.
        this.visible = new Map<string, boolean>();
        this.messageService.add({severity:'warn', summary:'Daysheet order', detail:'The Daysheet Resource Order could not be loaded, so the calendar is showing every resource.'});
      }
      else {
        this.visible = this.itineraryService.fromDaysheetOrder(result.items).visible;
      }

      this.loadgrid();
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting Resources.'});
    });
  }

  //Keyed on both id and name: the id is what a booking carries, the name is the fallback for a
  //list that does not return one.
  private loadPackages() {
    this.http.get(bmxurl + "packagelist/" + this.VenueID + "/true").subscribe(response => {
      const packages = (response as any).data ?? [];

      this.packageColours = new Map<string, string>();

      packages.forEach((pkg: any) => {
        if (pkg.colour == null || pkg.colour == "") {
          return;
        }

        if (pkg.packageId != null) {
          this.packageColours.set(pkg.packageId.toString(), pkg.colour);
        }

        if (pkg.name != null) {
          this.packageColours.set(pkg.name, pkg.colour);
        }
      });

      this.loadgrid();
    }, error => {
      //Not fatal - the blocks just fall back to the resource colour.
    });
  }

  viewChanged() {
    this.calendarRef?.getApi()?.changeView(this.calendarView);
  }

  addDays(date: Date, days: number): Date {
    const moved = new Date(date);
    moved.setDate(moved.getDate() + days);
    return moved;
  }

  dateChange(days: number) {
    this.chosenDate = this.addDays(this.chosenDate, days);
    this.loadgrid();
  }

  dateSelected() {
    this.loadgrid();
  }

  editdata(bookingId: string) {
    this.router.navigateByUrl("/editbooking/" + bookingId);
  }

  //One day at a time. The rows are built by the same service the Itinerary view uses, so the two
  //screens always agree on what is running and when - and it unpacks the resource groups, which
  //is what the old code here was missing.
  loadgrid() {
    this.busyService.busy();

    const searchCrit = this.itineraryService.searchCriteria(
      this.VenueID,
      ConvertDateToDB(this.chosenDate),
      ConvertDateToDB(this.chosenDate),
      []);

    this.http.post(bmxurl + "/BookingList", searchCrit).subscribe(response => {
      const bookings = ((response as booking_returndata).data ?? [])
        .filter((b: any) => b.inProgress == false);

      this.bookingCount = bookings.length;

      //Not drawn until the Daysheet order is in, so a resource switched off there never flashes
      //up first - loadResources draws the grid when it lands.
      if (this.visible == null) {
        this.busyService.idle();
        return;
      }

      this.buildEvents(this.itineraryService.buildRows(bookings, null, this.visible));
      this.busyService.idle();
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error getting bookings', detail:'An error occurred getting Bookings.'});
    });
  }

  //An empty colour counts as not set, so it falls through to the next one rather than painting
  //a block with nothing.
  private colourOf(value: string): string {
    return (value == null || value.trim() == "") ? null : value;
  }

  //Taken from the daysheet, which colours its blocks the same way: dark writing on a pale
  //colour, light writing on a dark one.
  private textColourFor(colour: string): string {
    if (colour == null || colour == "") {
      return "#000000";
    }

    let hex = colour.replace("#", "").trim();

    if (hex.length == 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    if (hex.length != 6) {
      return "#000000";
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return ((r * 299) + (g * 587) + (b * 114)) / 1000 > 140 ? "#000000" : "#ffffff";
  }

  private buildEvents(rows: any[]) {
    const day = this.chosenDate.getFullYear()
      + "-" + ('00' + (this.chosenDate.getMonth() + 1)).slice(-2)
      + "-" + ('00' + this.chosenDate.getDate()).slice(-2);

    const resourceColours = new Map<string, string>();

    (this.resources ?? []).forEach(res => {
      resourceColours.set(this.itineraryService.idOf(res), res.colour);
    });

    const events = rows
      .filter(row => row.timeslot != "")
      .map(row => {
        //The resource the booking is in decides the colour, so the grid reads by room. The
        //package is only a fallback for a resource with no colour set, and the default catches
        //the case where neither has one.
        const colour = this.colourOf(resourceColours.get(row.resourceId))
          ?? this.colourOf(this.packageColours.get(row.packageId))
          ?? this.colourOf(this.packageColours.get(row.packageName))
          ?? "#8B5CF6";

        return {
          //No offset on either end, so the times are drawn as the venue wrote them down rather
          //than being moved by whatever zone the browser is in.
          start: day + "T" + row.timeslot + ":00",
          end: row.endtime == "" ? undefined : day + "T" + row.endtime + ":00",
          title: row.resourceName + " · " + (row.name || row.bookingIdHR) + " (" + row.people + ")",
          backgroundColor: colour,
          borderColor: colour,
          //Package colours run from pale to near black, so the writing flips to suit.
          textColor: this.textColourFor(colour),
          extendedProps: { bookingId: row.bookingId }
        };
      });

    this.calendarOptions = {
      ...this.calendarOptions,
      initialView: this.calendarView,
      initialDate: day,
      events: events,
      eventClick: (arg: any) => this.editdata(arg.event.extendedProps.bookingId)
    };

    //initialDate only counts when the calendar starts up, so on every later change the grid has
    //to be sent to the new day through the API - otherwise the events were reloaded for the date
    //picked while the grid carried on showing the one before.
    this.calendarRef?.getApi()?.gotoDate(day);
  }
}
